import validator from "validator";

class EmailUtils {
  /**
   * Inlines CSS rules from every `<style>` block in the document onto
   * matching elements as inline styles, then returns `doc.body.innerHTML`.
   *
   * This is needed for the port-call notification send path: the original
   * template uses class-based rules (.completed, .success, .failed) that
   * Lexical strips when it re-serialises the HTML.  Inlining ensures the
   * styling survives the Lexical round-trip and arrives intact in Outlook.
   *
   * Only handles flat selectors (element, .class, element.class,
   * element[attr]).  Media queries and pseudo-selectors are skipped safely.
   */
  static inlineHtmlStyles(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const rules: Array<{ selector: string; declarations: string }> = [];

    doc.querySelectorAll("style").forEach((styleEl) => {
      const cssText = styleEl.textContent ?? "";
      // Match selector { declarations } — skip @-rules and nested blocks
      const ruleRegex = /([^@{}][^{}]*)\{([^{}]+)\}/g;
      let match: RegExpExecArray | null;
      while ((match = ruleRegex.exec(cssText)) !== null) {
        const selector = match[1].trim();
        const declarations = match[2].trim();
        if (selector && declarations) {
          rules.push({ selector, declarations });
        }
      }
    });

    rules.forEach(({ selector, declarations }) => {
      try {
        doc.querySelectorAll<HTMLElement>(selector).forEach((el) => {
          const existing = el.style.cssText;
          el.style.cssText = existing ? `${existing}; ${declarations}` : declarations;
        });
      } catch {
        // Skip selectors that the browser cannot parse (e.g. pseudo-elements)
      }
    });

    // Remove <style> tags — styles are now inline
    doc.querySelectorAll("style").forEach((el) => el.remove());

    return doc.body.innerHTML;
  }

  static isValidEmail(email: string) {
    try {
      // Trim the email first
      const trimmedEmail = email?.trim() || "";
      
      // Empty after trim = invalid (or valid if you want to allow optional fields)
      if (trimmedEmail === "") return true; // Change to `false` if email is required
      
      // Must start with alphanumeric
      if (!/^[a-zA-Z0-9]/.test(trimmedEmail)) return false;
      
      return validator.isEmail(trimmedEmail, {
        allow_display_name: false,
        require_tld: true,
        allow_utf8_local_part: false,
        require_display_name: false,
        allow_ip_domain: false,
        domain_specific_validation: true,
      });
    } catch (error) {
      console.log(error);
      return false;
    }
  }
}

export default EmailUtils;